import { withCartBodyLinkIds } from './solstice__cart-link-id.js';
import { CartLinesUpdateEvent } from '@shopify/events';

// Swym's own "Add to cart" buttons (wishlist drawer, save-for-later popup) call
// Shopify's cart API directly (via fetch or XHR, depending on the flow) and never
// dispatch the theme's CartLinesUpdateEvent, so cart-drawer/cart-icon never learn
// the cart changed until the next page load. This bridges that gap by watching for
// Swym's own real /cart/add request to finish, then dispatching the same event
// product-form.js dispatches after a normal add-to-cart.

const CART_ADD_PATTERN = /\/cart\/add(\.js)?(\?|$)/;
const FALLBACK_TIMEOUT_MS = 5000;

/** @type {((value?: unknown) => void) | null} */
let pendingCartAdd = null;

function resolvePendingCartAdd() {
  if (!pendingCartAdd) return;
  const resolve = pendingCartAdd;
  pendingCartAdd = null;
  resolve();
}

const originalFetch = window.fetch;
window.fetch = function (...args) {
  const resource = args[0];
  const url = typeof resource === 'string' ? resource : resource instanceof Request ? resource.url : null;

  if (url && CART_ADD_PATTERN.test(url) && pendingCartAdd && args[1]?.body) {
    args[1] = { ...args[1], body: withCartBodyLinkIds(args[1].body) };
  }
  const request = originalFetch.apply(this, args);

  if (url && CART_ADD_PATTERN.test(url) && pendingCartAdd) {
    request.then(resolvePendingCartAdd, resolvePendingCartAdd);
  }

  return request;
};

const originalXhrOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  this.__isCartAdd = typeof url === 'string' && CART_ADD_PATTERN.test(url);
  return originalXhrOpen.call(this, method, url, ...rest);
};

const originalXhrSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.send = function (...args) {
  if (this.__isCartAdd && pendingCartAdd) {
    args[0] = withCartBodyLinkIds(args[0]);
    this.addEventListener('loadend', resolvePendingCartAdd, { once: true });
  }
  return originalXhrSend.apply(this, args);
};

// Capture phase so this arms pendingCartAdd before Swym's own click handler
// (bound directly to the button) issues its request.
document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target.closest('[class*="swym"][class*="add-to-cart"]') : null;
    if (!target) return;

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [],
        promise: deferredEventPromise.promise,
      })
    );

    const cartAddDetected = new Promise((resolve) => {
      pendingCartAdd = resolve;
    });
    const fallbackTimeout = new Promise((resolve) => setTimeout(resolve, FALLBACK_TIMEOUT_MS));

    Promise.race([cartAddDetected, fallbackTimeout])
      .then(() => fetch(`${Theme.routes.cart_url}.js`))
      .then((response) => response.json())
      .then((cart) => {
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { source: 'swym-wishlist', items: cart.items },
        });
      })
      .catch(deferredEventPromise.reject);
  },
  true
);
